import React, { useState, forwardRef, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Typography,
  Grid,
} from '@mui/material';
import List from '@mui/material/List';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import { useForm, Controller } from 'react-hook-form';
import { maxLengthValidation } from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  assignPackageToChannel,
  createNewData,
  viewDataById,
} from '../../services/channel-packager';
import StyledDialogActions from '../../theme/dialogActionStyles';
import S3ImageUpload from '../common/S3ImageUpload';
import { getSearchListData } from '../../services/apps.service';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddChannelPackager = ({
  title,
  packageId,
  platform,
  onCancel,
  onSuccess,
  action,
  releaseNotes,
  version,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      version: '',
      notes: '',
    },
  });

  const packageUrl = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const saveNewData = (payload) => {
    createNewData(payload)
      .then(() => {
        onSuccess('New package uploaded successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    if (action === 'create') {
      if (packageUrl.current) {
        const payload = {
          name: packageUrl.current.split('/')[
            packageUrl.current.split('/').length - 1
          ],
          platform: platform === 'Roku' ? 1 : 2,
          version: data.version,
          notes: data.notes,
          url: packageUrl.current,
        };
        saveNewData(payload);
      } else {
        setFormSubmitted(false);
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'Please upload package file to proceed.',
        });
      }
    } else if (action !== 'create') {
      // assing channels
      setTimeout(() => {
        const payload = {
          channelIds: right.map((item) => item.id),
          packageId,
        };
        assignPackageToChannel(payload)
          .then(() => {
            onSuccess('Package applied successfully.');
          })
          .catch((err) => {
            const message =
              err.response?.data?.message ||
              'Something went wrong, please try again.';

            setSnackbarInfo({
              ...snackbarInfo,
              show: true,
              type: 'error',
              message,
            });
            setFormSubmitted(false);
          });
      }, 100);
    }
  };

  const updateImage = (url) => {
    packageUrl.current = url;
  };

  const getChannelData = async () => {
    const allChannels = await getSearchListData();
    const selectedItems = await viewDataById(packageId);
    const channelData = allChannels?.data || [];
    const selectedData = selectedItems?.data || [];
    setRight(selectedData);
    const leftData = channelData.filter(
      (item) => !selectedData.some((sel) => sel.id === item.id)
    );
    setLeft(leftData);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    setIsLoading(true);
    getChannelData();
  }, [packageId]);

  const not = (a, b) => a.filter((value) => !b.includes(value));

  const intersection = (a, b) => a.filter((value) => b.includes(value));

  const union = (a, b) => [...a, ...not(b, a)];

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const filterItems = (items, searchText) => {
    if (!searchText.trim()) return items;

    const text = searchText.toLowerCase();
    if (items.length === 0) return [];
    return items.filter(
      (item) =>
        item.code.toLowerCase().includes(text) ||
        item.title.toLowerCase().includes(text)
    );
  };

  const customList = (heading, items, searchValue, setSearchValue) => {
    const filteredItems = filterItems(items, searchValue);

    return (
      <Card>
        <CardHeader sx={{ px: 2, py: 1 }} title={heading} />
        <CardContent sx={{ px: 2, py: 1 }}>
          <Grid container alignItems="center">
            <Checkbox
              onClick={handleToggleAll(filteredItems)}
              checked={
                numberOfChecked(filteredItems) === filteredItems.length &&
                filteredItems.length !== 0
              }
              indeterminate={
                numberOfChecked(filteredItems) !== filteredItems.length &&
                numberOfChecked(filteredItems) !== 0
              }
              disabled={filteredItems.length === 0}
            />
            <Typography variant="body2" color="text.secondary">
              <Typography component="span" fontWeight="bold">
                {numberOfChecked(filteredItems)}/{filteredItems.length} selected
              </Typography>
            </Typography>
          </Grid>
        </CardContent>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search…"
          fullWidth
          sx={{ p: 1 }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <Divider />
        <List
          sx={{
            width: 500,
            height: 350,
            bgcolor: 'background.paper',
            overflow: 'auto',
          }}
          dense
          component="div"
          role="list"
        >
          {filteredItems.map((value) => {
            const labelId = `transfer-list-item-${value}-label`;

            return (
              <ListItemButton
                key={value.code}
                role="listitem"
                onClick={handleToggle(value)}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.includes(value)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={value.title} />
              </ListItemButton>
            );
          })}
        </List>
      </Card>
    );
  };

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="add-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        <form id="add-packager-form" onSubmit={handleSubmit(onFormSubmit)}>
          {action === 'create' ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="version"
                  control={control}
                  rules={{
                    ...maxLengthValidation(20),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Version"
                      type="text"
                      error={!!errors.version}
                      helperText={errors?.version?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="notes"
                  control={control}
                  rules={{
                    ...maxLengthValidation(255),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Release Notes"
                      type="text"
                      error={!!errors.notes}
                      helperText={errors?.notes?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container spacing={2} sx={{ marginBottom: 10 }}>
                <Grid item xs={12} sm={12} md={12}>
                  <Typography variant="body2" color="text.secondary">
                    <Typography component="span" fontWeight="bold">
                      Version: {version}
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <Typography variant="body2" color="text.secondary">
                    <Typography component="span" fontWeight="bold">
                      Release Notes: {releaseNotes}
                    </Typography>
                  </Typography>
                </Grid>
              </Grid>
              {!isLoading && (
                <Grid
                  container
                  spacing={2}
                  sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <Grid
                    container
                    spacing={2}
                    sx={{ justifyContent: 'center', alignItems: 'center' }}
                  >
                    <Grid>
                      {customList(
                        'Available channels for assignment',
                        left,
                        leftSearch,
                        setLeftSearch
                      )}
                    </Grid>
                    <Grid>
                      <Grid
                        container
                        direction="column"
                        sx={{ alignItems: 'center' }}
                      >
                        <Button
                          sx={{ my: 0.5, mx: 2 }}
                          variant="outlined"
                          size="small"
                          onClick={handleCheckedRight}
                          disabled={leftChecked.length === 0}
                          aria-label="move selected right"
                        >
                          &gt;
                        </Button>
                        <Button
                          sx={{ my: 0.5, mx: 2 }}
                          variant="outlined"
                          size="small"
                          onClick={handleCheckedLeft}
                          disabled={rightChecked.length === 0}
                          aria-label="move selected left"
                        >
                          &lt;
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid>
                      {customList(
                        'Currently assigned channels',
                        right,
                        rightSearch,
                        setRightSearch
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12} sm={12} md={12}>
                <Typography variant="body2" color="text.secondary">
                  <Typography component="span" fontWeight="bold">
                    Note: Save action is required to apply the package to the
                    selected channels.
                  </Typography>
                </Typography>
              </Grid>
            </>
          )}
        </form>
        {action === 'create' && (
          <Grid item xs={12} sm={12} md={12}>
            <S3ImageUpload
              setSnackbarInfo={setSnackbarInfo}
              updateImage={updateImage}
              isPackage
            />
          </Grid>
        )}
      </DialogContent>

      <StyledDialogActions>
        <Grid>
          <Button
            type="submit"
            form="add-packager-form"
            color="primary"
            variant="contained"
            // disabled={formSubmitted}
            sx={{ marginRight: 2 }}
          >
            Save
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={onCancel}
            disabled={formSubmitted}
          >
            Cancel
          </Button>
        </Grid>
      </StyledDialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddChannelPackager.propTypes = {
  title: PropTypes.string.isRequired,
  packageId: PropTypes.number.isRequired,
  platform: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  action: PropTypes.string.isRequired,
  releaseNotes: PropTypes.string,
  version: PropTypes.string,
};

AddChannelPackager.defaultProps = {
  releaseNotes: '',
  version: '',
};

export default AddChannelPackager;
